<script setup lang="ts">
import EditorJS from '@editorjs/editorjs'
import type { OutputData, EditorConfig } from '@editorjs/editorjs'
// @ts-ignore
import Paragraph from '@editorjs/paragraph'
// @ts-ignore
import NestedList from '@editorjs/nested-list'
// @ts-ignore
import ImageTool from '@editorjs/image'
// @ts-ignore
import Marker from '@editorjs/marker'
const props = defineProps<{
    modelValue: OutputData
    disabled?: boolean
}>()

const holderId = ''.concat('editorjs', '-').concat(Math.random().toString(36).substr(2, 9))

const emit = defineEmits(['update:modelValue', 'raise:error'])

const onRaiseError = (error: string) => {
    emit('raise:error', error)
}

const syncedValue = computed({
    get: () => {
        if (props.modelValue === undefined) {
            emit('update:modelValue', {})
            return {}
        }
        return props.modelValue
    },
    set: (value) => {
        emit('update:modelValue', value)
    },
})

const EditorJSInstance = ref<EditorJS | null>(null)
const EditorIsLoading = ref(true)

const saveData = async () => {
    EditorJSInstance.value
        ?.save()
        .then((outputData) => {
            syncedValue.value = outputData
        })
        .catch((error) => {
            console.error('Saving failed: ', error)
        })
}
const editorHeight = ref(200)
const drag = (e: MouseEvent) => {
    console.log('drag')
    const holder = document.getElementById(holderId)
    const holderHeight = holder?.offsetHeight
    const offsetY = e.offsetY
    const height = holderHeight ? holderHeight + offsetY : 200
    editorHeight.value = height
}
const touchStarted = ref(false)
const touchStartedY = ref(0)
const touchStart = (e: TouchEvent) => {
    console.log('touchStart')
    touchStarted.value = true
    touchStartedY.value = e.changedTouches[0].pageY
}
const touchMove = (e: TouchEvent) => {
    console.log('touchMove')
    if (touchStarted.value) {
        const holder = document.getElementById(holderId)
        const offsetY = e.changedTouches[0].pageY - touchStartedY.value
        const height = editorHeight.value + offsetY
        editorHeight.value = height
    }
}
const touchEnd = (e: TouchEvent) => {
    console.log('touchEnd')
    touchStarted.value = false
    touchStartedY.value = 0
    const holder = document.getElementById(holderId)
    editorHeight.value = holder?.offsetHeight || 200
}

onMounted(() => {
    const options: EditorConfig = {
        readOnly: props.disabled,
        holder: holderId,
        tools: {
            paragraph: {
                class: Paragraph,
                inlineToolbar: true,
            },
            nestedList: {
                class: NestedList,
                inlineToolbar: true,
            },
            marker: {
                class: Marker,
                shortcut: 'CMD+SHIFT+M',
            },
        },
        onChange: async () => {
            saveData()
        },
        i18n: {
            messages: {
                ui: {
                    blockTunes: {
                        toggler: {
                            'Click to tune': 'クリックして調整',
                            'or drag to move': 'またはドラッグして移動',
                        },
                    },
                    inlineToolbar: {
                        converter: {
                            'Convert to': '変換',
                        },
                    },
                    toolbar: {
                        toolbox: {
                            Add: '追加',
                        },
                    },
                    popover: {
                        'Filter': 'フィルター',
                        'Nothing found': '見つかりませんでした',
                    },
                },
                blockTunes: {
                    delete: {
                        'Delete': '削除',
                        'Click to delete': 'クリックして削除',
                    },
                    moveUp: {
                        'Move up': '上へ移動',
                    },
                    moveDown: {
                        'Move down': '下へ移動',
                    },
                },
                toolNames: {
                    Text: 'テキスト',
                    Heading: '見出し',
                    List: 'リスト',
                    NestedList: 'ネストリスト',
                    Link: 'リンク',
                    Marker: 'マーカー',
                    Bold: '太字',
                    Italic: '斜体',
                    Image: '画像',
                },
                tools: {
                    header: {
                        'Header': '見出し',
                        'Heading 1': '見出し1',
                        'Heading 2': '見出し2',
                        'Heading 3': '見出し3',
                        'Heading 4': '見出し4',
                        'Heading 5': '見出し5',
                        'Heading 6': '見出し6',
                    },
                    paragraph: {
                        'Enter something': 'テキストを入力',
                    },
                    list: {
                        Ordered: '数字付き',
                        Unordered: '記号付き',
                    },
                    image: {
                        'Caption': 'キャプション',
                        'Select an Image': '画像を選択',
                        'With border': '枠線付き',
                        'Stretch image': '画像を伸ばす',
                        'With background': '背景付き',
                    },
                    link: {
                        'Add a link': 'リンクを追加',
                    },
                },
            },
        },
    }
    if (syncedValue.value) {
        options.data = syncedValue.value as OutputData
    }
    EditorJSInstance.value = new EditorJS(options)
    EditorJSInstance.value.isReady.then(() => {
        console.log('Editor.js is ready to work!')
        EditorIsLoading.value = false
    })
})
</script>

<template>
    <div class="bg-white rounded-md border shadow-sm relative">
        <div
            v-show="!EditorIsLoading"
            v-if="!props.disabled"
            :id="holderId"
            class="p-4 overflow-y-scroll w-full flex-grow"
            :style="{ height: `${editorHeight}px` }"
        />
        <div
            :class="`cursor-row-resize hover:bg-slate-100 bg-slate-50/50 p-0 flex hadow-sm absolute bottom-0 left-0 right-0 h-4
           before:bg-black before:content-' ' before:absolute before:w-[20px] before:h-[1px] after:bg-black after:content-' ' after:absolute after:w-[20px] after:h-[1px]
            before:inset-x-0 before:top-0 before:mx-auto before:mt-[8px] after:inset-x-0 after:top-0 after:mx-auto after:mt-[5px]
           `"
            :draggable="true"
            @drag="drag"
            @touchstart.prevent="touchStart"
            @touchmove.prevent="touchMove"
            @touchend.prevent="touchEnd"
        />
    </div>
</template>

<style></style>
